import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SalesService } from '@/services/sales.service';
import { Lead, LeadStatus } from '@/models/lead.model';
import { useAuth } from '@/contexts/AuthContext';
import { Button, FormInput, FormSelect, FormTextarea } from '@/components/common';
import { Building2, User, Mail, Phone, Globe, TrendingUp, DollarSign, FileText, ArrowLeft, Save } from 'lucide-react';

const leadStatuses: LeadStatus[] = ['new', 'contacted', 'qualified', 'proposal', 'closed-won', 'closed-lost'];
const leadSources = ['Website', 'Referral', 'Cold Call', 'Email', 'Trade Show', 'Social Media', 'Other'];

export const LeadForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState<Partial<Lead>>({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    source: 'Website',
    status: 'new',
    assignedTo: profile?.id || '',
    value: undefined,
    notes: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: lead, isLoading } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => id ? SalesService.getLead(id) : null,
    enabled: isEditing && !!id,
  });

  React.useEffect(() => {
    if (lead) {
      setFormData(lead);
    } else if (!isEditing && profile?.id) {
      // Ensure assignedTo is set for new leads
      setFormData(prev => ({
        ...prev,
        assignedTo: profile.id,
      }));
    }
  }, [lead, isEditing, profile]);

  const mutation = useMutation({
    mutationFn: async (data: Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>) => {
      console.log('[LeadForm] Starting mutation:', {
        isEditing,
        id,
        data,
        timestamp: new Date().toISOString(),
      });

      try {
        if (isEditing && id) {
          console.log('[LeadForm] Updating lead:', { id, data });
          await SalesService.updateLead(id, data);
          console.log('[LeadForm] Lead updated successfully:', { id });
        } else {
          console.log('[LeadForm] Creating new lead:', { data });
          const leadId = await SalesService.createLead(data);
          console.log('[LeadForm] Lead created successfully:', { leadId });
        }
      } catch (error: any) {
        console.error('[LeadForm] Error in mutation:', {
          error,
          errorMessage: error?.message,
          errorCode: error?.code,
          stack: error?.stack,
          data,
        });
        throw error;
      }
    },
    onSuccess: () => {
      console.log('[LeadForm] Mutation successful, invalidating queries and navigating...');
      // Invalidate all leads queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      // Refetch immediately to show the new lead
      queryClient.refetchQueries({ queryKey: ['leads'] });
      navigate('/sales');
    },
    onError: (error: any) => {
      console.error('[LeadForm] Mutation error:', {
        error,
        errorMessage: error?.message,
        errorCode: error?.code,
        errorDetails: error?.details,
        stack: error?.stack,
      });
      
      // Show user-friendly error message
      const message = error?.message || 'An unknown error occurred while saving the lead.';
      setErrorMessage(message);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null); // Clear any previous errors
    
    console.log('[LeadForm] Form submitted:', {
      formData,
      isEditing,
      id,
    });

    // Validate required fields
    if (!formData.companyName?.trim()) {
      setErrorMessage('Company Name is required');
      return;
    }
    if (!formData.contactName?.trim()) {
      setErrorMessage('Contact Name is required');
      return;
    }
    if (!formData.email?.trim()) {
      setErrorMessage('Email is required');
      return;
    }
    if (!formData.assignedTo?.trim()) {
      setErrorMessage('Assigned To is required. Please ensure you are logged in.');
      return;
    }

    try {
      await mutation.mutateAsync(formData as Omit<Lead, 'id' | 'createdAt' | 'updatedAt'>);
    } catch (error: any) {
      // Error is already handled in onError callback
      console.error('[LeadForm] Submit error caught:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading lead...</p>
        </div>
      </div>
    );
  }

  const statusOptions = leadStatuses.map(status => ({
    value: status,
    label: status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' '),
  }));

  const sourceOptions = leadSources.map(source => ({
    value: source,
    label: source,
  }));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/sales')}
        >
          Back
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-8 h-8 text-blue-600" />
            {isEditing ? 'Edit Lead' : 'New Lead'}
          </h1>
          <p className="mt-1 text-gray-600">
            {isEditing ? 'Update lead information' : 'Create a new sales lead'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-6">
        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border-l-4 border-red-400 rounded-r-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-semibold text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{errorMessage}</p>
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setErrorMessage(null)}
                    className="text-sm font-medium text-red-800 hover:text-red-900 underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            label="Company Name"
            icon={Building2}
            required
            value={formData.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
            placeholder="Enter company name"
          />

          <FormInput
            label="Contact Name"
            icon={User}
            required
            value={formData.contactName}
            onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
            placeholder="Enter contact name"
          />

          <FormInput
            label="Email"
            type="email"
            icon={Mail}
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="contact@company.com"
          />

          <FormInput
            label="Phone"
            type="tel"
            icon={Phone}
            value={formData.phone || ''}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="(555) 123-4567"
          />

          <FormSelect
            label="Source"
            icon={Globe}
            options={sourceOptions}
            value={formData.source}
            onChange={(e) => setFormData({ ...formData, source: e.target.value })}
          />

          <FormSelect
            label="Status"
            icon={TrendingUp}
            options={statusOptions}
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value as LeadStatus })}
          />

          <FormInput
            label="Value ($)"
            type="number"
            step="0.01"
            icon={DollarSign}
            value={formData.value || ''}
            onChange={(e) => setFormData({ ...formData, value: e.target.value ? parseFloat(e.target.value) : undefined })}
            placeholder="0.00"
          />
        </div>

        <FormTextarea
          label="Notes"
          icon={FileText}
          rows={4}
          value={formData.notes || ''}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Add any additional notes about this lead..."
        />

        <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={() => navigate('/sales')}
            icon={ArrowLeft}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={Save}
            isLoading={mutation.isPending}
            iconPosition="right"
          >
            {isEditing ? 'Update Lead' : 'Create Lead'}
          </Button>
        </div>
      </form>
    </div>
  );
};

